import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Heading, styled } from '@grupoboticario/flora-react';

import { IProduct } from 'src/common/types/CatalogType';
import { CatalogContext } from 'src/contexts/Catalog';
import CloseIcon from '@material-ui/icons/Close';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import ProductDetails from 'src/components/organisms/ProductDetails';
import { MemoizedProductsList } from 'src/components/organisms/ProductsList';
import {
  getCurrentPagesNumber,
  sendProductClickEvent,
} from 'src/services/googleAnalytics/enhancedEcommerce';
import sendClickEvent from 'src/services/googleAnalytics/helpers/sendClickEvent';
import content from 'src/common/content';

const ProductsWrapper = styled('div', {
  width: '30%',
  height: 'calc(100vh - 140px)',
  marginTop: '64px',
  display: 'flex',
  flexDirection: 'column',
});

const HeaderWrapper = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '$4',

  button: {
    background: 'transparent',
  },
});

const ProductListWrapper = styled('div', {
  overflowY: 'auto',
  height: '100%',
});

const format = (page: number) =>
  page.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false,
  });

export interface IProductsSection {
  isOpen: boolean;
  onCloseClick: () => void;
}

export const DesktopProductsSection: FC<IProductsSection> = ({
  onCloseClick,
  isOpen,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<IProduct>();
  const [state] = useContext(CatalogContext);

  const resetSelectedProduct = () => {
    setSelectedProduct(undefined);
  };

  const handleBackButton = () => {
    resetSelectedProduct();
    sendClickEvent.goBackProductDetail();
  };

  useEffect(() => {
    resetSelectedProduct();
  }, [state.page]);

  const selectProductHandler = (
    product: IProduct,
    productPosition: number,
  ): void => {
    setSelectedProduct(product);
    sendProductClickEvent(
      product,
      getCurrentPagesNumber(state.exhibition.currentPagesData),
      productPosition,
    );
  };

  const headingMessage = useMemo(() => {
    const page1 =
      state.exhibition.currentPagesData[0] &&
      format(state.exhibition.currentPagesData[0].number);
    const page2 =
      state.exhibition.currentPagesData[1] &&
      format(state.exhibition.currentPagesData[1].number);

    return page2
      ? `Produtos nas páginas ${page1} e ${page2}:`
      : `Produtos na página ${page1}:`;
  }, [state.exhibition]);

  if (!isOpen) {
    return null;
  }

  const shouldShowResellerButton = !!content.products.findReseller;

  return (
    <ProductsWrapper>
      <HeaderWrapper>
        {!!selectedProduct ? (
          <Button
            variant="alt"
            rightIcon={<KeyboardBackspaceIcon />}
            label="Voltar para lista de produtos"
            onClick={handleBackButton}
            size="small"
          />
        ) : (
          <Heading level="6" data-testid="heading-message">
            {headingMessage}
          </Heading>
        )}
        <Button
          variant="alt"
          size="small"
          label="Fechar lista de produtos"
          leftIcon={<CloseIcon />}
          onClick={onCloseClick}
          aria-label="Fechar lista de produtos"
          data-testid="close-products-section"
        ></Button>
      </HeaderWrapper>

      <ProductListWrapper>
        {!!selectedProduct && (
          <ProductDetails
            product={selectedProduct}
            shouldShowResellerButton={shouldShowResellerButton}
            showPrice={content.products.showPrice}
          />
        )}

        <MemoizedProductsList
          isVisible={!selectedProduct}
          onSelectProduct={selectProductHandler}
        />
      </ProductListWrapper>
    </ProductsWrapper>
  );
};
